import { track } from "./lib/analytics";

interface ContactExpertProps {
  /** Human-readable step label the customer is on when they reach out. */
  stepName: string;
  /** Threads the handoff to the same GA4/n8n record as the rest of the funnel. */
  submissionId: string;
}

type Channel = "tel" | "email";

/**
 * Persistent "Talk to an expert" affordance, rendered in each step's nav row
 * (between Back and Continue) on every step — including embed mode, where the
 * footer (and its phone numbers) is hidden, so this is the only human-contact
 * out (issue #31).
 *
 * A native <details> disclosure: keyboard-operable and screen-reader labelled for
 * free. The panel opens upward as an absolute popover anchored to the .actions
 * row (see styles.css), so it never shifts the buttons and never changes body
 * height — the iframe auto-resize in app.tsx is untouched. Phone is the primary
 * channel; we surface both regional lines rather than picking one, with email as
 * the secondary option.
 *
 * Each channel click fires contact_expert_click (a human-handoff micro-conversion,
 * see #31 / #18) tagged with the step and submission id.
 */
export function ContactExpert({ stepName, submissionId }: ContactExpertProps) {
  const onContact = (channel: Channel) => () =>
    track("contact_expert_click", {
      step_name: stepName,
      channel,
      submission_id: submissionId,
    });

  return (
    <details class="contact-expert">
      <summary>
        <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
        </svg>
        <span>Talk to an expert</span>
      </summary>
      <div class="contact-expert-panel">
        <p class="contact-expert-lead">
          Questions? Reach a Kasselman Solar generator specialist — no obligation.
        </p>
        <a class="contact-expert-line" href="tel:+15184788365" onClick={onContact("tel")}>
          <span class="contact-expert-region">Upstate</span>
          <span class="contact-expert-value">(518) 478-8365</span>
        </a>
        <a class="contact-expert-line" href="tel:+18456814088" onClick={onContact("tel")}>
          <span class="contact-expert-region">Hudson Valley</span>
          <span class="contact-expert-value">(845) 681-4088</span>
        </a>
        <a
          class="contact-expert-line"
          href="mailto:info@kasselmansolar.com"
          onClick={onContact("email")}
        >
          <span class="contact-expert-region">Email</span>
          <span class="contact-expert-value">info@kasselmansolar.com</span>
        </a>
      </div>
    </details>
  );
}
